/**
 * Typed Campaign Settings view (#186, ADR-0004 §5/§6).
 *
 * A dedicated, purpose-built settings surface — NOT the generic fact-row
 * editor. Campaign-scoped settings live in the "config" bucket of the
 * three-bucket model (fact / setting / definition): a fixed, code-typed
 * key/value set written to the op log as `config.set` ops under the
 * reserved `__campaign` subject (`packages/shared/config-ops.ts`). Each
 * control here dispatches one `config.set` op through the sync client;
 * the value reads back from the folded `Model.config` record (the
 * `replica/entities-rebuilt` fold), so edits are optimistic, offline-
 * capable, and live across devices/co-GMs over the WS.
 *
 * The "editor = reader" invariant deliberately does NOT bind here — that
 * invariant governs *content facts*. Settings are GM-only operational
 * data, so a bespoke typed form is correct, not a violation.
 *
 * Controls (one per `CampaignConfigSchema` key, ADR-0004 §6):
 *   - `accent_color`        → color picker + hex text (hex is the
 *                             non-color a11y cue).
 *   - `canonical_calendar`  → calendar-DEFINITION picker (bundled +
 *     `display_calendars`     folded `pack.define_calendar` registry).
 *   - `prose_voice`,        → enum `<select>`.
 *     `campaign_kind`,
 *     `status`
 *   - `setting_summary`     → multiline rich_text editor (stored as text).
 *   - `hero_image`          → asset-id picker (text id; upload lands with
 *                             the asset pipeline #135).
 *   - `world_name`          → text.
 *   - `locale`, `time_zone` → text (BCP-47 / IANA tags).
 *
 * a11y: every control is keyboard-reachable with a visible label + focus
 * ring; color is never the only signal (the hex string mirrors the
 * swatch); every string flows through `t()`.
 */

import { loadBundledCalendars } from '@dl/calendar';
import {
  ConfigProseVoiceSchema,
  isHexColor,
  type ConfigState,
  type GmPrefProfile,
  type LocalizedString,
} from '@dl/shared';
import { card, colorPicker, selectInput, textArea, textInput } from '@dl/ui';
import {
  branch,
  div,
  h2,
  input,
  label,
  p,
  show,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { campaignAccentSwatches, colorPickerMessages } from '../color-swatches.js';
import { getHomebrewDefs } from '../entities/homebrew-defs.js';
import { t } from '../i18n.js';

/**
 * The minimal Model slice the view reads: the folded campaign config and
 * the session mode (the settings surface is GM-only, but the gate lives in
 * the host's `branch` — this slice keeps the view decoupled from the full
 * `Model`). A generic so the host's wider `Model` satisfies it structurally.
 */
export interface CampaignSettingsSlice {
  config: ConfigState;
  session: { mode: string };
}

/**
 * The host contract: a single dispatcher that turns a typed config change
 * into a `config.set` op submission. The host (app.ts) owns op minting +
 * `syncClient.submit`; this view only knows the typed key/value pair. The
 * payload is built by the caller's `configSet(key, value)` so the per-key
 * value type is enforced at the call site (an enum key cannot carry a
 * mistyped value past the discriminated union).
 */
export type SetConfig<Msg> = <K extends keyof ConfigState>(key: K, value: NonNullable<ConfigState[K]>) => Msg;

/** Resolve a LocalizedString to display text for the active locale. */
function localizedName(value: LocalizedString | undefined): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  const short = locale.split('-')[0] ?? 'en';
  return value[locale] ?? value[short] ?? value['en'] ?? Object.values(value)[0] ?? '';
}

/** A `{ id, name }` calendar choice for the canonical/display pickers. */
interface CalendarChoice {
  id: string;
  name: string;
}

/**
 * The calendar definitions a campaign may point `canonical_calendar` /
 * `display_calendars` at: the bundled starter calendars PLUS the folded
 * `pack.define_calendar` registry overlay (ADR-0004 §4). A registry entry
 * shadows a bundled one of the same id (the homebrew definition wins, same
 * precedence as `loadActiveCalendar`). Names are localized.
 */
export function calendarChoices(): CalendarChoice[] {
  const byId = new Map<string, CalendarChoice>();
  // Bundled first so a registry definition can override the display name.
  try {
    for (const cal of loadBundledCalendars()) {
      byId.set(cal.id, { id: cal.id, name: localizedName(cal.label) || cal.id });
    }
  } catch {
    // A malformed bundled pack should not blank the picker — registry
    // definitions still populate it.
  }
  for (const def of getHomebrewDefs().calendars) {
    byId.set(def.id, { id: def.id, name: localizedName(def.name) || def.id });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Read a string-valued config key (empty string when unset). */
function configString(s: CampaignSettingsSlice, key: keyof ConfigState): string {
  const v = s.config[key];
  return typeof v === 'string' ? v : '';
}

// ─── Color (accent_color) ──────────────────────────────────────────────

/**
 * Accent-color picker — the shared `@dl/ui` `colorPicker` primitive (#219):
 * a curated swatch row (the §10.2.1 campaign-accent palette) over a native
 * `<input type="color">` + a hex text field. The hex string and the active
 * swatch's check glyph are the non-color a11y cues (structural invariant:
 * color is never the only signal). Every control commits the same
 * `#rrggbb`/`#rrggbbaa` value, validated through `isHexColor` before the op
 * is dispatched (a curated swatch is always valid; a malformed manual hex is
 * dropped). A blank value is left as-is (no soft-delete for settings — LWW
 * per key; clearing is out of scope).
 */
function viewAccentColor<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const currentHex = state.map((s) => configString(s, 'accent_color'));
  const commit = (raw: string): void => {
    const value = raw.trim();
    if (value === '' || !isHexColor(value)) return;
    send(setConfig('accent_color', value));
  };
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-accent-color',
    children: [
      label({ for: 'settings-accent-color-hex', class: 'settings-label' }, [
        text(t('settings.campaign.accent_color.label')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.accent_color.help'))]),
      colorPicker({
        value: currentHex,
        swatches: campaignAccentSwatches(),
        onChange: commit,
        messages: colorPickerMessages(),
        id: 'settings-accent-color',
        testid: 'settings-accent-color-picker',
        hexTestid: 'config-accent_color',
      }),
    ],
  });
}

// ─── Plain text keys (world_name, locale, time_zone, hero_image) ────────

/**
 * A single-line text setting. Commits on blur + Enter (no live op per
 * keystroke). A blank value is dropped (LWW; clearing a setting is out of
 * scope for this increment).
 */
function viewTextSetting<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
  opts: {
    key: 'world_name' | 'locale' | 'time_zone' | 'hero_image';
    labelKey: string;
    helpKey: string;
    placeholderKey: string;
  },
): Mountable {
  const id = `settings-${opts.key}`;
  const commit = (raw: string): void => {
    const value = raw.trim();
    if (value === '') return;
    send(setConfig(opts.key, value));
  };
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: `settings-${opts.key}`,
    children: [
      label({ for: id, class: 'settings-label' }, [text(t(opts.labelKey))]),
      p({ class: 'settings-help' }, [text(t(opts.helpKey))]),
      textInput({
        type: 'text',
        value: state.map((s) => configString(s, opts.key)),
        placeholder: t(opts.placeholderKey),
        testid: `config-${opts.key}`,
        ariaLabel: t(opts.labelKey),
        onChange: (value: string) => commit(value),
        attrs: {
          id,
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'Enter') commit((event.target as HTMLInputElement).value);
          },
        },
      }),
    ],
  });
}

// ─── Rich text (setting_summary) ────────────────────────────────────────

/**
 * Multi-line setting-summary editor. The value kind is `rich_text` but the
 * stored value is a markdown string; a plain `<textarea>` is the Phase 1
 * editor (the WYSIWYG primitive lands with the fact-row rich_text work).
 * Commits on blur.
 */
function viewSettingSummary<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-setting_summary',
    children: [
      label({ for: 'settings-setting_summary', class: 'settings-label' }, [
        text(t('settings.campaign.setting_summary.label')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.setting_summary.help'))]),
      textArea({
        className: 'settings-summary-textarea',
        rows: 4,
        value: state.map((s) => configString(s, 'setting_summary')),
        placeholder: t('settings.campaign.setting_summary.placeholder'),
        testid: 'config-setting_summary',
        ariaLabel: t('settings.campaign.setting_summary.label'),
        onChange: (value: string) => {
          send(setConfig('setting_summary', value));
        },
        attrs: { id: 'settings-setting_summary' },
      }),
    ],
  });
}

// ─── Enum selects (prose_voice, campaign_kind, status) ──────────────────

/**
 * Render an enum `<select>` for a config key. `selectInput` takes a static
 * value, so a `branch` on the current value re-mounts the control when the
 * folded config changes — each arm passes its literal value (no render-body
 * `.peek()`; peek-in-slot lint). The unset arm shows the placeholder option.
 */
function viewEnumSetting<Slice extends CampaignSettingsSlice, Msg, V extends string>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
  opts: {
    key: 'prose_voice' | 'campaign_kind' | 'status';
    labelKey: string;
    helpKey: string;
    options: readonly V[];
    optionLabel: (value: V) => string;
    parse: (raw: string) => V | undefined;
  },
): Mountable {
  const id = `settings-${opts.key}`;
  const renderSelect = (value: string): Mountable[] => [
    selectInput({
      id,
      name: opts.key,
      testid: `config-${opts.key}`,
      ariaLabel: t(opts.labelKey),
      value,
      size: 'full',
      options: [
        { value: '', label: t('settings.campaign.unset') },
        ...opts.options.map((v) => ({ value: v, label: opts.optionLabel(v) })),
      ],
      onChange: (next) => {
        const parsed = opts.parse(next);
        // `opts.key` is a union of literal keys, so TS cannot prove `parsed`
        // matches the per-key value type at this generic boundary; the value
        // IS validated by `opts.parse` against that key's vocabulary. Narrow
        // through `never` (the same cast the host dispatcher uses for the
        // key-typed `configSet`).
        if (parsed !== undefined) send(setConfig(opts.key, parsed as never));
      },
    }),
  ];
  // Discriminant: '' (unset) or one of the literal enum values.
  const arms: Record<string, () => Mountable[]> = { '': () => renderSelect('') };
  for (const v of opts.options) arms[v] = () => renderSelect(v);
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: `settings-${opts.key}`,
    children: [
      label({ for: id, class: 'settings-label' }, [text(t(opts.labelKey))]),
      p({ class: 'settings-help' }, [text(t(opts.helpKey))]),
      branch(
        state.map((s) => {
          const v = s.config[opts.key];
          return typeof v === 'string' && opts.options.includes(v as V) ? (v as string) : '';
        }),
        arms,
      ),
    ],
  });
}

// ─── Calendar pickers (canonical_calendar, display_calendars) ───────────

/**
 * Canonical-calendar picker — a `<select>` of every resolvable calendar
 * DEFINITION (bundled + folded `pack.define_calendar`). The stored value is
 * the calendar id; the active calendar is re-derived from this pointer in
 * the host's fold (`resolveActiveCalendarId`). A `branch` on the current id
 * re-mounts the static-value `selectInput`.
 */
function viewCanonicalCalendar<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const choices = calendarChoices();
  const ids = choices.map((c) => c.id);
  const renderSelect = (value: string): Mountable[] => [
    selectInput({
      id: 'settings-canonical_calendar',
      name: 'canonical_calendar',
      testid: 'config-canonical_calendar',
      ariaLabel: t('settings.campaign.canonical_calendar.label'),
      value,
      size: 'full',
      options: [
        { value: '', label: t('settings.campaign.unset') },
        ...choices.map((c) => ({ value: c.id, label: c.name })),
      ],
      onChange: (next) => {
        if (next !== '' && ids.includes(next)) send(setConfig('canonical_calendar', next));
      },
    }),
  ];
  const arms: Record<string, () => Mountable[]> = { '': () => renderSelect('') };
  for (const id of ids) arms[id] = () => renderSelect(id);
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-canonical_calendar',
    children: [
      label({ for: 'settings-canonical_calendar', class: 'settings-label' }, [
        text(t('settings.campaign.canonical_calendar.label')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.canonical_calendar.help'))]),
      branch(
        state.map((s) => {
          const v = s.config.canonical_calendar;
          return typeof v === 'string' && ids.includes(v) ? v : '';
        }),
        arms,
      ),
    ],
  });
}

/**
 * Display-calendars picker — a checkbox per resolvable calendar definition.
 * Toggling one writes the full `display_calendars` array (LWW per key).
 * Each checkbox pairs its state with a visible label (no color-only signal).
 */
function viewDisplayCalendars<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const choices = calendarChoices();
  const currentSet = (s: Slice): readonly string[] =>
    Array.isArray(s.config.display_calendars) ? s.config.display_calendars : [];
  const toggle = (s: Slice, id: string, checked: boolean): void => {
    const set = new Set(currentSet(s));
    if (checked) set.add(id);
    else set.delete(id);
    send(setConfig('display_calendars', [...set]));
  };
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-display_calendars',
    children: [
      h2({ class: 'settings-label' }, [text(t('settings.campaign.display_calendars.label'))]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.display_calendars.help'))]),
      ...choices.map((c) => {
        const fieldId = `settings-display_calendars-${c.id}`;
        return label({ for: fieldId, class: 'settings-checkbox-row' }, [
          input({
            type: 'checkbox',
            id: fieldId,
            'data-testid': `config-display_calendars-${c.id}`,
            checked: state.map((s) => currentSet(s).includes(c.id)),
            'aria-label': c.name,
            onChange: (event: Event) => {
              const checked = (event.target as HTMLInputElement).checked;
              // Read the current array off the peeked state at click time.
              toggle(state.peek(), c.id, checked);
            },
          }),
          text(c.name),
        ]);
      }),
    ],
  });
}

// ─── LLM kill switch (llm_kill_switch, #218) ───────────────────────────

/**
 * Per-campaign LLM kill switch (#218). One click stops all AI generation for
 * this campaign — the GM-facing half of the cost-control kill switch (the
 * Account-wide admin switch lives in Admin → LLM). Writes the
 * `llm_kill_switch` config.set op; the worker AI Gateway reads the projected
 * override and refuses every task before any provider call or spend.
 *
 * a11y: engaged/disengaged is signalled three ways — the checkbox state, the
 * status text (ON / OFF), and a leading ● / ✕ glyph — so colour is never the
 * only cue. The status line's class toggles the colour treatment on top.
 */
function viewLlmKillSwitch<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const engaged = state.map((s) => s.config.llm_kill_switch === true);
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-llm-kill-switch',
    children: [
      label({ for: 'settings-llm-kill-switch-input', class: 'settings-label' }, [
        text(t('settings.campaign.llm_kill_switch.label')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.llm_kill_switch.help'))]),
      label({ for: 'settings-llm-kill-switch-input', class: 'settings-checkbox-row' }, [
        input({
          type: 'checkbox',
          id: 'settings-llm-kill-switch-input',
          'data-testid': 'config-llm_kill_switch',
          // Checked = kill switch engaged = AI OFF.
          checked: engaged,
          'aria-label': t('settings.campaign.llm_kill_switch.toggle'),
          onChange: (event: Event) => {
            send(setConfig('llm_kill_switch', (event.target as HTMLInputElement).checked));
          },
        }),
        text(t('settings.campaign.llm_kill_switch.toggle')),
      ]),
      div(
        {
          class: engaged.map((e) =>
            e ? 'settings-killswitch-status is-engaged' : 'settings-killswitch-status is-clear',
          ),
          'data-testid': 'llm-kill-switch-status',
          role: 'status',
        },
        [
          text(
            engaged.map((e) =>
              e
                ? t('settings.campaign.llm_kill_switch.off')
                : t('settings.campaign.llm_kill_switch.on'),
            ),
          ),
        ],
      ),
    ],
  });
}

// ─── LLM pending lane (#223) ────────────────────────────────────────────

const LANE_MODES = ['off', 'surface', 'silent'] as const;

/** Read an array-valued config key as a comma-joined string (empty when unset). */
function configCsv(s: CampaignSettingsSlice, key: keyof ConfigState): string {
  const v = s.config[key];
  return Array.isArray(v) ? (v as string[]).join(', ') : '';
}

/** Parse a comma/whitespace-separated list into a deduped, trimmed id array. */
function parseCsvIds(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (id !== '') seen.add(id);
  }
  return [...seen];
}

/**
 * The LLM pending-lane controls (#223, `04-llm-role.md` §4.1.1): a mode
 * `<select>` (`off` / `surface` / `silent`) plus two comma-separated id
 * editors — the predicate **allowlist** and the per-entity **gm_only_drafts**
 * opt-out. These are op-log `config.set` settings (ADR-0004 §5), GM-only. The
 * lane promotes an eligible LLM draft to `pending` via a `system`-authored
 * `fact.set_status` in the DO; the LLM never authors `pending`/`canon`.
 *
 * Functional, deliberately un-polished (a chip/token editor is a later UI
 * pass): the array editors are CSV text inputs that commit on blur/Enter.
 */
function viewLlmPendingLane<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const renderModeSelect = (value: string): Mountable[] => [
    selectInput({
      id: 'settings-llm_pending_lane_mode',
      name: 'llm_pending_lane_mode',
      testid: 'config-llm_pending_lane_mode',
      ariaLabel: t('settings.campaign.llm_pending_lane_mode.label'),
      value,
      size: 'full',
      options: LANE_MODES.map((m) => ({
        value: m,
        label: t(`settings.campaign.llm_pending_lane_mode.option.${m}`),
      })),
      onChange: (next) => {
        if (LANE_MODES.includes(next as (typeof LANE_MODES)[number])) {
          send(setConfig('llm_pending_lane_mode', next as (typeof LANE_MODES)[number]));
        }
      },
    }),
  ];
  const modeArms: Record<string, () => Mountable[]> = {};
  for (const m of LANE_MODES) modeArms[m] = () => renderModeSelect(m);
  // Default to 'off' when unset (the lane default).
  modeArms[''] = () => renderModeSelect('off');

  const csvEditor = (
    key: 'llm_pending_allowlist' | 'gm_only_drafts',
    labelKey: string,
    helpKey: string,
    placeholderKey: string,
  ): Mountable => {
    const id = `settings-${key}`;
    const commit = (raw: string): void => {
      send(setConfig(key, parseCsvIds(raw) as never));
    };
    return card({
      padding: 'lg',
      className: 'settings-section',
      testid: `settings-${key}`,
      children: [
        label({ for: id, class: 'settings-label' }, [text(t(labelKey))]),
        p({ class: 'settings-help' }, [text(t(helpKey))]),
        textInput({
          type: 'text',
          value: state.map((s) => configCsv(s, key)),
          placeholder: t(placeholderKey),
          testid: `config-${key}`,
          ariaLabel: t(labelKey),
          onChange: (value: string) => commit(value),
          attrs: {
            id,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'Enter') commit((event.target as HTMLInputElement).value);
            },
          },
        }),
      ],
    });
  };

  return div({ class: 'settings-llm-pending-lane' }, [
    card({
      padding: 'lg',
      className: 'settings-section',
      testid: 'settings-llm_pending_lane_mode',
      children: [
        label({ for: 'settings-llm_pending_lane_mode', class: 'settings-label' }, [
          text(t('settings.campaign.llm_pending_lane_mode.label')),
        ]),
        p({ class: 'settings-help' }, [
          text(t('settings.campaign.llm_pending_lane_mode.help')),
        ]),
        branch(
          state.map((s) => {
            const v = s.config.llm_pending_lane_mode;
            return typeof v === 'string' && LANE_MODES.includes(v as (typeof LANE_MODES)[number])
              ? v
              : '';
          }),
          modeArms,
        ),
      ],
    }),
    csvEditor(
      'llm_pending_allowlist',
      'settings.campaign.llm_pending_allowlist.label',
      'settings.campaign.llm_pending_allowlist.help',
      'settings.campaign.llm_pending_allowlist.placeholder',
    ),
    csvEditor(
      'gm_only_drafts',
      'settings.campaign.gm_only_drafts.label',
      'settings.campaign.gm_only_drafts.help',
      'settings.campaign.gm_only_drafts.placeholder',
    ),
  ]);
}

// ─── GM preference profile (#250, 04-llm-role.md §16) ──────────────────

/**
 * The explicit GM-authored preference fields, in render order. Each is a
 * free-text dimension of the `gm_pref_profile` config object (the EXPLICIT
 * layer — a setting, never canon). `content_boundaries` is a hard line the
 * model must respect (GM-only, never inferred); `free_form` is open notes.
 */
const GM_PREF_FIELDS = [
  'tone',
  'naming',
  'vocabulary',
  'pacing',
  'style_references',
  'content_boundaries',
  'free_form',
] as const satisfies readonly (keyof GmPrefProfile)[];

/** Read the current explicit profile object (empty object when unset). */
function gmPrefProfile(s: CampaignSettingsSlice): GmPrefProfile {
  const v = s.config.gm_pref_profile;
  return v && typeof v === 'object' ? v : {};
}

/** Read one explicit-profile field as a string (empty when unset). */
function gmPrefField(s: CampaignSettingsSlice, key: keyof GmPrefProfile): string {
  const v = gmPrefProfile(s)[key];
  return typeof v === 'string' ? v : '';
}

/**
 * The GM preference profile section (#250): the explicit, GM-authored style
 * layer (one textarea per dimension) plus the opt-in toggle for the inferred
 * (signals-derived) layer.
 *
 * - **Explicit layer.** Each field commits the WHOLE `gm_pref_profile` object
 *   with that key updated (the config value is the object — LWW per config key).
 *   A blank field is removed from the object (so it contributes no prompt line).
 *   The current object is read from the live state at commit time (`.peek()` in
 *   the handler — allowed outside reactive slots).
 * - **Opt-in gate.** `gm_pref_inference_enabled` is a boolean, default OFF
 *   (§16.3 "AI is always optional"). Unchecked = inference off; the inferred
 *   layer never composes into the prompt. a11y: ON/OFF is signalled three ways
 *   (checkbox, status text, ● / ✕ glyph), never colour alone.
 */
function viewGmPrefProfile<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  const commitField = (key: keyof GmPrefProfile, raw: string): void => {
    const value = raw.trim();
    const current = gmPrefProfile(state.peek());
    const next: GmPrefProfile = { ...current };
    if (value === '') delete next[key];
    else next[key] = value;
    send(setConfig('gm_pref_profile', next));
  };

  const fieldCards = GM_PREF_FIELDS.map((key) => {
    const id = `settings-gm_pref-${key}`;
    return card({
      padding: 'lg',
      className: 'settings-section',
      testid: `settings-gm_pref-${key}`,
      children: [
        label({ for: id, class: 'settings-label' }, [
          text(t(`settings.campaign.gm_pref.${key}.label`)),
        ]),
        p({ class: 'settings-help' }, [text(t(`settings.campaign.gm_pref.${key}.help`))]),
        textArea({
          className: 'settings-gm-pref-textarea',
          rows: 2,
          value: state.map((s) => gmPrefField(s, key)),
          placeholder: t(`settings.campaign.gm_pref.${key}.placeholder`),
          testid: `config-gm_pref-${key}`,
          ariaLabel: t(`settings.campaign.gm_pref.${key}.label`),
          onChange: (value: string) => commitField(key, value),
          attrs: { id },
        }),
      ],
    });
  });

  const inferenceOn = state.map((s) => s.config.gm_pref_inference_enabled === true);

  const inferenceToggle = card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-gm_pref-inference',
    children: [
      label({ for: 'settings-gm_pref-inference-input', class: 'settings-label' }, [
        text(t('settings.campaign.gm_pref.inference.label')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.campaign.gm_pref.inference.help'))]),
      label({ for: 'settings-gm_pref-inference-input', class: 'settings-checkbox-row' }, [
        input({
          type: 'checkbox',
          id: 'settings-gm_pref-inference-input',
          'data-testid': 'config-gm_pref_inference_enabled',
          checked: inferenceOn,
          'aria-label': t('settings.campaign.gm_pref.inference.toggle'),
          onChange: (event: Event) => {
            send(
              setConfig('gm_pref_inference_enabled', (event.target as HTMLInputElement).checked),
            );
          },
        }),
        text(t('settings.campaign.gm_pref.inference.toggle')),
      ]),
      div(
        {
          class: inferenceOn.map((on) =>
            on ? 'settings-inference-status is-on' : 'settings-inference-status is-off',
          ),
          'data-testid': 'gm-pref-inference-status',
          role: 'status',
        },
        [
          text(
            inferenceOn.map((on) =>
              on
                ? t('settings.campaign.gm_pref.inference.on')
                : t('settings.campaign.gm_pref.inference.off'),
            ),
          ),
        ],
      ),
    ],
  });

  return div({ class: 'settings-gm-pref', 'data-testid': 'settings-gm-pref-profile' }, [
    h2({ class: 'settings-subheading' }, [text(t('settings.campaign.gm_pref.heading'))]),
    p({ class: 'settings-help' }, [text(t('settings.campaign.gm_pref.help'))]),
    ...fieldCards,
    inferenceToggle,
  ]);
}

// ─── The view ───────────────────────────────────────────────────────────

const PROSE_VOICES = ['default', 'gritty', 'whimsical', 'pulp', 'noir', 'high_fantasy'] as const;
const CAMPAIGN_KINDS = ['one_shot', 'mini_arc', 'long_running', 'sandbox', 'episodic'] as const;
const CAMPAIGN_STATUSES = ['active', 'archived'] as const;

/**
 * The typed Campaign Settings panel. Mounts every config control; each
 * dispatches a `config.set` op via `setConfig`. GM-only: the host renders
 * this only inside the GM-gated settings tab, but the view also guards with
 * a `show` so a stray mount degrades safely.
 */
export function viewCampaignSettings<Slice extends CampaignSettingsSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  setConfig: SetConfig<Msg>,
): Mountable {
  return div(
    {
      class: 'settings-tab-panel',
      'data-testid': 'settings-tab-campaign',
      id: 'settings-campaign-panel',
      role: 'tabpanel',
    },
    [
      show(
        state.map((s) => s.session.mode === 'gm'),
        () => [
          viewTextSetting(state, send, setConfig, {
            key: 'world_name',
            labelKey: 'settings.campaign.world_name.label',
            helpKey: 'settings.campaign.world_name.help',
            placeholderKey: 'settings.campaign.world_name.placeholder',
          }),
          viewAccentColor(state, send, setConfig),
          viewEnumSetting(state, send, setConfig, {
            key: 'campaign_kind',
            labelKey: 'settings.campaign.campaign_kind.label',
            helpKey: 'settings.campaign.campaign_kind.help',
            options: CAMPAIGN_KINDS,
            optionLabel: (v) => t(`settings.campaign.campaign_kind.option.${v}`),
            parse: (raw) => (CAMPAIGN_KINDS.includes(raw as (typeof CAMPAIGN_KINDS)[number])
              ? (raw as (typeof CAMPAIGN_KINDS)[number])
              : undefined),
          }),
          viewEnumSetting(state, send, setConfig, {
            key: 'status',
            labelKey: 'settings.campaign.status.label',
            helpKey: 'settings.campaign.status.help',
            options: CAMPAIGN_STATUSES,
            optionLabel: (v) => t(`settings.campaign.status.option.${v}`),
            parse: (raw) => (CAMPAIGN_STATUSES.includes(raw as (typeof CAMPAIGN_STATUSES)[number])
              ? (raw as (typeof CAMPAIGN_STATUSES)[number])
              : undefined),
          }),
          viewEnumSetting(state, send, setConfig, {
            key: 'prose_voice',
            labelKey: 'settings.campaign.prose_voice.label',
            helpKey: 'settings.campaign.prose_voice.help',
            options: PROSE_VOICES,
            optionLabel: (v) => t(`settings.campaign.prose_voice.option.${v}`),
            parse: (raw) => {
              const parsed = ConfigProseVoiceSchema.safeParse(raw);
              return parsed.success ? parsed.data : undefined;
            },
          }),
          viewLlmKillSwitch(state, send, setConfig),
          viewLlmPendingLane(state, send, setConfig),
          viewGmPrefProfile(state, send, setConfig),
          viewSettingSummary(state, send, setConfig),
          viewCanonicalCalendar(state, send, setConfig),
          viewDisplayCalendars(state, send, setConfig),
          viewTextSetting(state, send, setConfig, {
            key: 'hero_image',
            labelKey: 'settings.campaign.hero_image.label',
            helpKey: 'settings.campaign.hero_image.help',
            placeholderKey: 'settings.campaign.hero_image.placeholder',
          }),
          viewTextSetting(state, send, setConfig, {
            key: 'locale',
            labelKey: 'settings.campaign.locale.label',
            helpKey: 'settings.campaign.locale.help',
            placeholderKey: 'settings.campaign.locale.placeholder',
          }),
          viewTextSetting(state, send, setConfig, {
            key: 'time_zone',
            labelKey: 'settings.campaign.time_zone.label',
            helpKey: 'settings.campaign.time_zone.help',
            placeholderKey: 'settings.campaign.time_zone.placeholder',
          }),
        ],
      ),
    ],
  );
}
